import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_INVENTORY, ADJUST_STOCK, TOGGLE_INVENTORY_MANAGEMENT } from '../graphql/mutations';
import { GET_CATALOG_ITEMS_AND_CATEGORIES } from '../graphql/queries';
import { X, Plus, Minus, AlertTriangle, Package } from 'lucide-react';

const InventoryManagement = ({ item, onClose, catalogId }) => {
    const [variants, setVariants] = useState([]);
    const [manageInventory, setManageInventory] = useState(item.manageInventory || false);
    const [hasSaveChanges, setHasSaveChanges] = useState(false);

    const [updateInventory, { loading: updateLoading }] = useMutation(UPDATE_INVENTORY);
    const [adjustStock, { loading: adjustLoading }] = useMutation(ADJUST_STOCK);
    const [toggleInventoryManagement] = useMutation(TOGGLE_INVENTORY_MANAGEMENT);

    useEffect(() => {
        // Initialize variants from item
        if (item && item.priceVariants) {
            setVariants(item.priceVariants.map(v => ({
                ...v,
                stock: v.stock || 0,
                sku: v.sku || '',
                lowStockThreshold: v.lowStockThreshold || 5
            })));
        }
        setManageInventory(item.manageInventory || false);
    }, [item]);

    // Track if there are unsaved changes
    useEffect(() => {
        setHasSaveChanges(true);
    }, [variants, manageInventory]);

    const handleVariantChange = (index, field, value) => {
        const updatedVariants = [...variants];

        if (field === 'stock' || field === 'lowStockThreshold') {
            value = value === '' ? '' : (parseInt(value) || 0);
        }

        updatedVariants[index][field] = value;
        setVariants(updatedVariants);
    };

    const handleStockAdjustment = async (index, adjustment) => {
        try {
            await adjustStock({
                variables: {
                    itemId: item.id,
                    variantIndex: index,
                    adjustment
                },
                refetchQueries: [
                    { query: GET_CATALOG_ITEMS_AND_CATEGORIES, variables: { catalogId } }
                ]
            });

            // Update local state to reflect change immediately
            const updatedVariants = [...variants];
            const currentStock = updatedVariants[index].stock || 0;
            updatedVariants[index].stock = Math.max(0, currentStock + adjustment);
            setVariants(updatedVariants);

        } catch (error) {
            console.error('Failed to adjust stock:', error);
        }
    };

    const handleToggleInventoryManagement = async () => {
        try {
            const { data } = await toggleInventoryManagement({
                variables: {
                    itemId: item.id,
                    manageInventory: !manageInventory
                }
            });

            if (data.toggleInventoryManagement.success) {
                setManageInventory(!manageInventory);
            }
        } catch (error) {
            console.error('Failed to toggle inventory management:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Remove typename from the variants array
            const updatedVariants = variants.map(variant => {
                const { __typename, ...rest } = variant;
                return {
                    ...rest,
                    stock: rest.stock === '' ? 0 : rest.stock,
                    lowStockThreshold: rest.lowStockThreshold === '' ? 0 : rest.lowStockThreshold
                };
            });

            // Update inventory with the modified variants
            await updateInventory({
                variables: {
                    itemId: item.id,
                    variants: updatedVariants
                },
                refetchQueries: [
                    { query: GET_CATALOG_ITEMS_AND_CATEGORIES, variables: { catalogId } }
                ]
            });
            setHasSaveChanges(false);
            onClose();
        } catch (error) {
            console.error('Failed to update inventory:', error);
        }
    };

    const getLowStockStatus = (variant) => {
        if (!manageInventory) return null;

        const stock = variant.stock || 0;
        const threshold = variant.lowStockThreshold || 5;

        if (stock <= 0) return "out";
        if (stock < threshold) return "low";
        return "normal";
    };

    return (
        <div className="fixed inset-0 z-50 p-4 md:flex md:items-center md:justify-center">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={hasSaveChanges && manageInventory ? undefined : onClose}></div>

            {/* Modal */}
            <div className="relative w-full max-w-md  mx-auto bg-white rounded-lg shadow-lg overflow-hidden md:max-w-2xl lg:max-w-4xl">
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center">
                        <Package size={20} className="text-[#191970] mr-2" />
                        <h2 className="text-xl font-bold text-[#191970]">Inventory Management</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full bg-gray-50 hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-700">
                            Manage your inventory levels and get notified when stock is running low.
                        </p>
                    </div>

                    <div className="mb-6">
                        <label className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={manageInventory}
                                onChange={handleToggleInventoryManagement}
                                className="form-checkbox h-5 w-5 text-[#191970] rounded"
                            />
                            <span className="ml-2 text-gray-700 bg-white font-medium">Track inventory for this item</span>
                        </label>
                    </div>

                    {manageInventory && (
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <h3 className="font-medium text-gray-700 mb-2">Stock Information</h3>

                                {/* Mobile view - Card layout */}
                                <div className="block md:hidden space-y-4">
                                    {variants.map((variant, index) => {
                                        const stockStatus = getLowStockStatus(variant);

                                        return (
                                            <div
                                                key={index}
                                                className={`p-4 rounded-lg border ${stockStatus === "out" ? "border-red-200 bg-red-50" :
                                                    stockStatus === "low" ? "border-amber-200 bg-amber-50" :
                                                        "border-gray-200"
                                                    }`}
                                            >
                                                <h4 className="font-medium mb-2">{variant.variant || 'Default'}</h4>

                                                <div className="grid grid-cols-2 gap-3 mb-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">SKU</label>
                                                        <input
                                                            type="text"
                                                            value={variant.sku || ''}
                                                            onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                                                            className="border bg-white rounded p-2 w-full text-sm"
                                                            placeholder="SKU"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Low Stock Alert</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={variant.lowStockThreshold === '' ? '' : (variant.lowStockThreshold || 5)}
                                                            onChange={(e) => handleVariantChange(index, 'lowStockThreshold', e.target.value)}
                                                            className="border bg-white rounded p-2 w-full text-sm"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between mt-2">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 mb-1">Current Stock</label>
                                                        <div className="flex items-center">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={variant.stock === '' ? '' : (variant.stock || 0)}
                                                                onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                                                                className="border bg-white rounded px-3 py-2 w-24 text-center mr-2"
                                                            />

                                                            {stockStatus === "out" && (
                                                                <span className="text-red-600 text-xs flex items-center">
                                                                    <AlertTriangle size={12} className="mr-1" />
                                                                    Out of stock
                                                                </span>
                                                            )}
                                                            {stockStatus === "low" && (
                                                                <span className="text-amber-600 text-xs flex items-center">
                                                                    <AlertTriangle size={12} className="mr-1" />
                                                                    Low stock
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex space-x-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStockAdjustment(index, -1)}
                                                            disabled={variant.stock <= 0 || adjustLoading}
                                                            className={`w-12 h-12 flex items-center justify-center rounded-full ${variant.stock <= 0
                                                                ? 'bg-gray-100 text-gray-400'
                                                                : 'bg-red-50 text-red-700 hover:bg-red-100'
                                                                }`}
                                                            aria-label="Decrease stock"
                                                        >
                                                            <Minus size={20} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStockAdjustment(index, 1)}
                                                            disabled={adjustLoading}
                                                            className="w-12 h-12 flex items-center justify-center rounded-full bg-green-50 text-green-700 hover:bg-green-100"
                                                            aria-label="Increase stock"
                                                        >
                                                            <Plus size={20} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Desktop/tablet view - Table layout */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="min-w-full table-auto">
                                        <thead>
                                            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                                                <th className="px-6 py-3">Variant</th>
                                                <th className="px-6 py-3">SKU</th>
                                                <th className="px-6 py-3">In Stock</th>
                                                <th className="px-6 py-3">Low Stock Alert</th>
                                                <th className="px-6 py-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {variants.map((variant, index) => {
                                                const stockStatus = getLowStockStatus(variant);

                                                return (
                                                    <tr
                                                        key={index}
                                                        className={
                                                            stockStatus === "out" ? "bg-red-50" :
                                                                stockStatus === "low" ? "bg-amber-50" :
                                                                    ""
                                                        }
                                                    >
                                                        <td className="px-6 py-4">{variant.variant || 'Default'}</td>
                                                        <td className="px-6 py-4">
                                                            <input
                                                                type="text"
                                                                value={variant.sku || ''}
                                                                onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                                                                className="border bg-white rounded p-2 w-full"
                                                                placeholder="SKU"
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={variant.stock === '' ? '' : (variant.stock || 0)}
                                                                    onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                                                                    className="border bg-white rounded p-2 w-24"
                                                                />

                                                                {stockStatus === "out" && (
                                                                    <span className="ml-2 text-red-600 text-xs flex items-center">
                                                                        <AlertTriangle size={12} className="mr-1" />
                                                                        Out of stock
                                                                    </span>
                                                                )}
                                                                {stockStatus === "low" && (
                                                                    <span className="ml-2 text-amber-600 text-xs flex items-center">
                                                                        <AlertTriangle size={12} className="mr-1" />
                                                                        Low stock
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={variant.lowStockThreshold === '' ? '' : (variant.lowStockThreshold || 5)}
                                                                onChange={(e) => handleVariantChange(index, 'lowStockThreshold', e.target.value)}
                                                                className="border bg-white rounded p-2 w-24"
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleStockAdjustment(index, 1)}
                                                                    disabled={adjustLoading}
                                                                    className="px-3 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 flex items-center"
                                                                >
                                                                    <Plus size={14} className="mr-1" /> Add
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleStockAdjustment(index, -1)}
                                                                    className="px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 flex items-center"
                                                                    disabled={variant.stock <= 0 || adjustLoading}
                                                                >
                                                                    <Minus size={14} className="mr-1" /> Remove
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-500 bg-gray-50 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    {manageInventory && hasSaveChanges && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={updateLoading}
                            className="px-4 py-2 bg-[#191970] text-white rounded hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#191970]"
                        >
                            {updateLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InventoryManagement;